import { useAccountDrawer } from 'context/accountDrawer';
import { useExecuteMultipleTasks } from 'pages/accountDrawer/startMultipleTasksContext';
import { useCallback } from 'react';

/**
 * What to do once a contact has been re-assigned to another company.
 *
 * Re-assigning stops the contact's strategy and its tasks on the BE, so the
 * task card the user was on is closed either way. Outside a flow the user just
 * wants to land on the company they picked.
 */
export const useAfterContactReassigned = () => {
  const { openAccountDrawer, updateDrawerContent } = useAccountDrawer();
  const { isFlowActive, onTaskHandled } = useExecuteMultipleTasks();

  return useCallback(
    (newAccountId?: string) => {
      if (isFlowActive) {
        // Follow the contact to its new company in place: the flow only
        // cancels itself when the open modal or task changes, so re-pointing
        // just the account keeps the flow alive. Without it the drawer stays
        // pinned to the account that no longer exists — and once the flow has
        // nothing left to advance to, it stays there for good.
        //
        // Must run before advancing: the flow ending re-writes the drawer
        // content, and this update would otherwise overwrite that with the
        // task tab it captured.
        if (newAccountId) {
          updateDrawerContent({ accountId: newAccountId });
        }
        // The task is gone, so close its card: a queue moves on to the next
        // task and ends when there is none, while a single task just closes —
        // never stepping to another task the user did not open.
        onTaskHandled('abandoned');
        return;
      }
      if (newAccountId) {
        openAccountDrawer({ accountId: newAccountId });
      }
    },
    [isFlowActive, onTaskHandled, openAccountDrawer, updateDrawerContent],
  );
};
